/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * App shell: Sentinelle-branded sidebar (desktop) / bottom tab bar (mobile).
 *
 * ── WHY THE NAV SHRANK FROM SEVEN TO FOUR ───────────────────────────────────
 * It used to list Home, Plan, Recipes, Shopping, Family, Circles and Settings
 * as peers. Two problems, one of them a real bug:
 *
 *   1. Seven peer destinations is past the point where a nav is scannable. The
 *      four that matter are a weekly loop — today, plan it, find recipes, buy
 *      the food — and the other three are things you touch once a month.
 *
 *   2. On mobile all seven rendered in a single `flex gap-1` row with no
 *      wrapping and no scroll. Laid out, that is roughly 500px of content in
 *      ~350px of phone. It overflowed. The nav was, literally, broken on the
 *      device this app is most used on.
 *
 * So: four primary destinations, and Household / Cooking groups / Settings /
 * appearance move into an account menu. Nothing was removed — every route still
 * exists and is one tap away.
 *
 * ── AND WHY MOBILE NAV MOVED TO THE BOTTOM ──────────────────────────────────
 * It was a text row pinned to the top of the screen: the hardest place to reach
 * one-handed, on an app whose two most important mobile moments — cooking and
 * shopping — are explicitly one-handed. It is now a bottom tab bar in the thumb
 * zone, with 44px targets and safe-area padding so it clears the home indicator.
 */

import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "@tanstack/react-query";

import { ChefLauncher, ChefPanel } from "../features/chef/ChefPanel.tsx";

import type { HomeResponse } from "../api/types.ts";
import { useToday } from "../lib/useToday.tsx";
import { usePrefs } from "../i18n/index.tsx";
import { Banner } from "./ui.tsx";
import { Icon, type IconName } from "./Icon.tsx";
import { ActionMenu, type ActionItem } from "./ActionMenu.tsx";
import { ErrorBoundary } from "./ErrorBoundary.tsx";

/**
 * The weekly loop, in the order it happens — plus the social side.
 *
 * Groups sits at the end rather than in the loop, because it is not part of it:
 * the first four are how a week gets planned, shopped and cooked, and the fifth
 * is who you talk to about it. It carries the unread badge, which is the whole
 * reason it earns a primary slot — a social feature nobody is told about is a
 * social feature nobody uses.
 */
const NAV: Array<{ to: string; key: string; icon: IconName; badge?: "circles" }> = [
  { to: "/", key: "nav.home", icon: "home" },
  { to: "/plan", key: "nav.plan", icon: "calendar" },
  { to: "/recipes", key: "nav.recipes", icon: "book" },
  { to: "/shopping", key: "nav.shopping", icon: "cart" },
  { to: "/circles", key: "nav.circles", icon: "chat", badge: "circles" },
];

/**
 * Unread group posts, read from the Home payload already in the query cache.
 *
 * Deliberately NOT its own fetch. The badge is chrome — it must never be the
 * reason a request goes out, and it must never show a spinner. If Today has not
 * been loaded yet the badge is simply absent, which is correct: we genuinely do
 * not know yet, and guessing zero and then popping a number in is worse than
 * showing nothing for a moment.
 */
function useUnreadGroups(): number {
  const qc = useQueryClient();
  const home = qc.getQueryData<HomeResponse>(["home"]);
  return home?.circles?.unreadCount ?? 0;
}

function UnreadDot({ count, className = "" }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={`grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold text-paper ${className}`}
      aria-label={`${count} unread`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function SidebarItem({ to, labelKey, icon, badge }: {
  to: string; labelKey: string; icon: IconName; badge?: number;
}) {
  const { t } = usePrefs();
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition ${
          isActive
            ? "bg-accent-soft font-medium text-accent"
            : "text-ink-2 hover:bg-paper-3 hover:text-ink"
        }`}
    >
      <Icon name={icon} className="h-[18px] w-[18px] shrink-0" />
      <span className="flex-1">{t(labelKey)}</span>
      {badge !== undefined && <UnreadDot count={badge} />}
    </NavLink>
  );
}

/**
 * Everything that is not the weekly loop.
 *
 * Theme and language live here rather than in the topbar. They were two
 * permanent buttons occupying the most valuable strip of the screen, in service
 * of a decision most people make once and never revisit.
 */
function AccountMenu({ align = "right" }: { align?: "left" | "right" }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t, theme, toggleTheme, lang, setLang } = usePrefs();

  // react-router's navigate, not window.location: assigning to pathname is a
  // full document reload, which would drop the react-query cache and re-run
  // the OIDC handshake on every trip to Settings.
  const go = (path: string) => () => navigate(path);

  const items: ActionItem[] = [
    { label: t("nav.family"), icon: "users", onSelect: go("/family") },
    // Cooking groups moved OUT of here and into the primary nav — a feature
    // reachable only through an account menu is a feature nobody finds.
    { label: t("nav.settings"), icon: "settings", onSelect: go("/settings") },
    {
      label: theme === "light" ? t("nav.theme.dark") : t("nav.theme.light"),
      icon: theme === "light" ? "moon" : "sun",
      onSelect: toggleTheme,
      hint: t("nav.appearance"),
    },
    {
      label: lang === "en" ? "Français" : "English",
      icon: "person",
      onSelect: () => setLang(lang === "en" ? "fr" : "en"),
      hint: t("nav.language"),
    },
    {
      label: t("common.signout"),
      icon: "logout",
      onSelect: () => void auth.signoutRedirect(),
      destructive: true,
    },
  ];

  const name = auth.user?.profile?.name
    ?? auth.user?.profile?.email
    ?? t("nav.account");

  return (
    <ActionMenu
      align={align}
      label={t("nav.account")}
      items={items}
      trigger={
        <span className="flex items-center gap-2">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent">
            {String(name).trim().charAt(0).toUpperCase() || "?"}
          </span>
          <span className="max-w-[8rem] truncate text-sm">{String(name)}</span>
        </span>
      }
    />
  );
}

/** Bottom tab bar. Mobile only — the sidebar owns navigation from `md` up. */
function TabBar() {
  const { t } = usePrefs();
  const unread = useUnreadGroups();
  return (
    <nav
      aria-label="Main"
      className="no-print sticky bottom-0 z-20 border-t border-rule-soft bg-paper/95 backdrop-blur md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="flex">
        {NAV.map((n) => (
          <li key={n.to} className="flex-1">
            <NavLink
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `relative flex min-h-[3.25rem] flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] transition ${
                  isActive ? "font-medium text-accent" : "text-ink-3"}`}
            >
              {({ isActive }) => (
                <>
                  <span className="relative">
                    <Icon name={n.icon} className={isActive ? "h-[22px] w-[22px]" : "h-5 w-5"} />
                    {n.badge === "circles" && (
                      <UnreadDot count={unread} className="absolute -right-2.5 -top-1.5" />
                    )}
                  </span>
                  {/* Five labels at 11px fit a 320px phone; truncate guards the
                      longest translation rather than trusting they all will. */}
                  <span className="max-w-full truncate px-0.5">{t(n.key)}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function Layout() {
  const { t } = usePrefs();
  const { today, timezone, clockWarning, error } = useToday();
  const { pathname } = useLocation();
  const unread = useUnreadGroups();
  const [chefOpen, setChefOpen] = useState(false);

  // ⌘K / Ctrl-K from anywhere. Guarded against firing while the user is typing
  // in a field that has its own meaning for it, and against the browser's own
  // shortcut by preventing the default.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setChefOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-full">
      <aside className="no-print hidden w-60 shrink-0 flex-col border-r border-rule-soft bg-paper-2 md:flex">
        {/* The full lockup, not the 36px square mark. sentinelle-logo.png is
            858x200 — sized by width so the wordmark stays legible instead of
            being squeezed into a thumbnail. */}
        <div className="px-5 py-5">
          <img src="/sentinelle-logo.png" alt="Sentinelle-IA"
               width={858} height={200} className="w-40 max-w-full" />
          <div className="mt-2 font-serif text-base font-semibold text-ink">MealPlanner</div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2" aria-label="Main">
          <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
            {t("nav.section.kitchen")}
          </div>
          {NAV.map((n) => (
            <SidebarItem key={n.to} to={n.to} labelKey={n.key} icon={n.icon}
                         badge={n.badge === "circles" ? unread : undefined} />
          ))}
        </nav>

        <div className="space-y-2 border-t border-rule-soft px-3 py-3">
          {/* Above the account menu, below the weekly loop: the assistant is
              not a destination, so it does not belong in the nav list, but it
              is used far more often than Settings. */}
          <ChefLauncher variant="sidebar" onOpen={() => setChefOpen(true)} />
          <AccountMenu align="left" />
          {/* The household's today, from the server — shown so a wrong date is
              visible rather than silently wrong. */}
          {today && (
            <div className="label-mono px-1 text-xs text-ink-3" title={`Timezone: ${timezone}`}>
              {today}
            </div>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print flex items-center justify-between border-b border-rule-soft bg-paper/80 px-4 py-2.5 backdrop-blur md:px-5">
          <div className="flex items-center gap-2 md:hidden">
            <img src="/sentinelle-mark-sm.png" alt="" width={144} height={144}
                 className="h-7 w-7 rounded-md" />
            <span className="font-serif font-semibold">MealPlanner</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {today && <span className="label-mono hidden text-xs text-ink-3 sm:inline">{today}</span>}
            <AccountMenu />
          </div>
        </header>

        <main className="flex-1 overflow-auto px-4 py-5 md:px-8 md:py-6">
          <div className="mx-auto max-w-6xl space-y-4">
            {clockWarning && <div className="no-print"><Banner tone="warn">{clockWarning}</Banner></div>}
            {error && (
              <div className="no-print">
                <Banner tone="warn">
                  Couldn't refresh the date from the server. Showing the last known day.
                </Banner>
              </div>
            )}
            {/* Scoped to the routed screen: a crash here leaves the sidebar,
                the nav, and every other route usable. */}
            <ErrorBoundary resetKey={pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>

        <ChefLauncher variant="fab" onOpen={() => setChefOpen(true)} />
        <TabBar />
      </div>

      <ChefPanel open={chefOpen} onClose={() => setChefOpen(false)} />
    </div>
  );
}
